import dayjs from 'dayjs';
import { Feature, Geometry } from 'geojson';
import { get, groupBy, range } from 'lodash';

import { getSoil } from '@/endpoints';
import { getCounties } from '@/endpoints/itasca/county/getCounties';
import getCDLStats from '@/endpoints/schoolcraft/cdl/getCDLStats';
import { SchoolcraftSoilResponse } from '@/endpoints/schoolcraft/soil/getSoil';
import { AcresFeature } from '@/models/features';
import { SoilIndex } from '@/models/soil/soil-type';
import { SoilProperties, SoilResponse } from '@/models/soils';
import { calculateCropPercentages } from '@/utils/insights/cropHistory';

import centroid from '../centroid';
import { nFormatter, round } from '../common';
import getAcres, { getArea } from '../getAcres';
import Logger from '../logger';
import { multiUnion } from '../multiUnion';

interface ISoilScore {
  type: SoilIndex;
  value: number;
}

interface ISoilAverages {
  STORIE: null | number;
  NCCPI: null | number;
  PI: null | number;
  CSR2: null | number;
  CPI: null | number;
  WAPI: null | number;
}

function getTotalAcres(feature: AcresFeature) {
  return `${nFormatter(getAcres(feature), 1)} acres`;
}

async function getUniqueCounties(feature: AcresFeature) {
  const c = centroid(feature.geometry);

  if (!c) {
    return [];
  }

  const [lng, lat] = c.geometry.coordinates;

  const { res } = await getCounties({
    location: { lat, lon: lng },
  });
  const resCounty = res?.data.results?.[0];

  if (resCounty) {
    return [`${resCounty.name}, ${resCounty.state_name}`];
  }

  return [];
}

/**
 * Groups the soils by mukey, multiplies the soil index by the amount of acres it covers, sum up the soil array, then sum the soil array.
 *  Now with the soil array, sum up the array again and divide by the total
 * @param groupedSoils
 * @param boundarySoils
 * @param soilIndex
 * @returns
 */
const getAverage = (
  groupedSoils: Feature<Geometry, SoilProperties>[][],
  boundarySoils: AcresFeature[],
  soilIndex: SoilIndex
) => {
  const combinedBoundaries = multiUnion(boundarySoils);

  if (!combinedBoundaries) return 0;

  const area = getArea(combinedBoundaries.geometry);

  return (
    groupedSoils
      .map((soil) =>
        soil
          .map(
            (feature) =>
              feature.properties[soilIndex] * getArea(feature.geometry)
          )
          .reduce((prev, curr) => prev + curr, 0)
      )
      .reduce((prev, curr) => prev + curr, 0) / area
  );
};

const getSoilAverages = (
  sd: SchoolcraftSoilResponse,
  boundarySoils: Feature[]
): ISoilAverages => {
  const { features } = sd;
  const groupedSoils = Object.values(
    groupBy(features, (f: (typeof features)[0]) => f.properties.mukey)
  ) as Feature<Geometry, SoilProperties>[][];

  const soilScores: ISoilAverages = {
    CPI: getAverage(groupedSoils, boundarySoils, 'CPI'),
    CSR2: getAverage(groupedSoils, boundarySoils, 'CSR2'),
    PI: getAverage(groupedSoils, boundarySoils, 'PI'),
    STORIE: getAverage(groupedSoils, boundarySoils, 'STORIE'),
    WAPI: getAverage(groupedSoils, boundarySoils, 'WAPI'),
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    NCCPI: getAverage(groupedSoils, boundarySoils, 'NCCPI'),
  };

  return soilScores;
};

async function getSoilScore(
  feature: AcresFeature,
  features: Feature[]
): Promise<ISoilScore> {
  const { res: resSoil, err: errSoil } = await getSoil({
    boundary: feature,
  });

  if (resSoil) {
    const soilAverages = getSoilAverages(resSoil.data, features);

    if (soilAverages.WAPI) {
      return {
        type: 'WAPI',
        value: soilAverages.WAPI,
      };
    }

    if (soilAverages.CSR2) {
      return {
        type: 'CSR2',
        value: soilAverages.CSR2,
      };
    }

    if (soilAverages.PI) {
      return {
        type: 'PI',
        value: soilAverages.PI,
      };
    }

    if (soilAverages.NCCPI) {
      return {
        type: 'NCCPI',
        value: soilAverages.NCCPI,
      };
    }
  }

  Logger.error(errSoil);

  return {
    type: 'NCCPI',
    value: 0,
  };
}

async function getTopCrops(feature: AcresFeature, threshold: number) {
  const start = dayjs().year();
  const end = 2015;
  const years = range(end, start + 1);
  const cdlStatsPromises = years.map((year) =>
    getCDLStats({ boundary: feature, year: String(year) })
  );
  const resolvedCDLStats = await Promise.all(cdlStatsPromises);
  const aggregatedCropPercentages = calculateCropPercentages(
    resolvedCDLStats
      .filter((item) => item.res && item.res.data)
      .map((item) => item.res!.data)
  ).cropPercentages;
  const topCrops = Object.entries(aggregatedCropPercentages)
    .filter((item) => item[1] >= threshold)
    .sort(([, a], [, b]) => b - a)
    .map((item) => item[0]);

  return topCrops;
}

function guessActiveKey(
  response: SoilResponse | SchoolcraftSoilResponse
): SoilIndex {
  const count = {
    CPI: 0,
    CSR2: 0,
    PI: 0,
    STORIE: 0,
    WAPI: 0,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    NCCPI: 0, // we want NCCPI to be last in list so that highestKey always returns the other score in the event of a tie
  };

  const features =
    (response as SoilResponse).feature_collection?.features ||
    (response as SchoolcraftSoilResponse).features;

  features.forEach((feature) => {
    Object.keys(count).forEach((k) => {
      if (feature.properties?.[k] || feature.properties?.[k] === 0) {
        count[k] += 1;
      }
    });
  });
  const firstValidKey = Object.keys(count).find((a) => {
    return !!count[a];
  });

  if (firstValidKey) {
    return firstValidKey as SoilIndex;
  }

  return 'NCCPI';
}

const getActiveSoilScore = (
  feature: Feature,
  activeSoilScoreIndex?: SoilIndex
): number => {
  if (!activeSoilScoreIndex) return 0;
  let newIndex = activeSoilScoreIndex;

  if (activeSoilScoreIndex === 'WAPI') {
    newIndex = 'WAPI';
  }

  const val = get(feature.properties, newIndex);

  if (val === undefined) return 0;

  return round(val, 1);
};

const getMaxSoilScore = (activeSoilScoreIndex?: SoilIndex): number => {
  switch (activeSoilScoreIndex) {
    case 'WAPI':
      return 200;
    case 'PI':
      return 147;
    case 'STORIE':
      return 100;
    default:
      return 100;
  }
};

const getAverageSoilScore = (
  soilAverages?: ISoilAverages | null,
  activeSoilScoreIndex?: SoilIndex
): number => {
  if (!soilAverages || !activeSoilScoreIndex) return 0;

  return round(soilAverages[activeSoilScoreIndex] || 0, 1);
};

export type { ISoilAverages, ISoilScore, SoilIndex };

export {
  getActiveSoilScore,
  getAverage,
  getAverageSoilScore,
  getMaxSoilScore,
  getSoilAverages,
  getSoilScore,
  getTopCrops,
  getTotalAcres,
  getUniqueCounties,
  guessActiveKey,
};
